import { useMemo } from 'react'
import styled from 'styled-components'

import { Numeric, Typography } from '@blade/ui-kit'
import { CurrencyModel } from '@root/entities/currency'
import { useOptionalAtom } from '@root/shared/lib'
import { useTranslation } from '@root/shared/localization'
import { Separator } from '@root/shared/ui-kit'

import type { PairModel } from '@root/entities/pair'

type Props = {
  pair: PairModel | undefined
}

export const PremiumCurrencyMetricsTable: React.FC<Props> = ({ pair }) => {
  const { t } = useTranslation()

  const price = useOptionalAtom(pair?.price)
  const liquidityUsd = useOptionalAtom(pair?.liquidityUsd)
  const marketCapUsd = useOptionalAtom(pair?.marketCapUsd)

  const fdv = useMemo(
    () =>
      pair && price
        ? CurrencyModel.calculateFdv(pair.quote, price.priceUsd)
        : undefined,
    [pair, price],
  )

  return (
    <Root>
      <PriceRow>
        <Price>
          <Label>{t('common.price')} USD</Label>
          <PriceValue>
            {price ? (
              <Numeric
                value={price.priceUsd}
                preset="currency-amount-short"
                currency="$"
              />
            ) : (
              '-'
            )}
          </PriceValue>
        </Price>
        <Price>
          <Label>{t('common.price')}</Label>
          <PriceValue>
            {price ? (
              <Numeric
                value={price.priceNative}
                preset="currency-amount-short"
                currency={pair?.base.ticker}
              />
            ) : (
              '-'
            )}
          </PriceValue>
        </Price>
      </PriceRow>
      <VolumeRow>
        <PriceVolume>
          <Label>{t('common.liquidity')}</Label>
          <VolumeValue>
            {liquidityUsd ? (
              <Numeric
                value={liquidityUsd}
                preset="currency-amount-short"
                currency="$"
              />
            ) : (
              '-'
            )}
          </VolumeValue>
        </PriceVolume>
        <Separator vertical />
        <PriceVolume>
          <Label>{t('common.fdv')}</Label>
          <VolumeValue>
            {fdv ? (
              <Numeric
                value={fdv}
                preset="currency-amount-short"
                currency="$"
              />
            ) : (
              '-'
            )}
          </VolumeValue>
        </PriceVolume>
        <Separator vertical />
        <PriceVolume>
          <Label>{t('trading.market-cap')}</Label>
          <VolumeValue>
            {marketCapUsd ? (
              <Numeric
                value={marketCapUsd}
                preset="currency-amount-short"
                currency="$"
              />
            ) : (
              '-'
            )}
          </VolumeValue>
        </PriceVolume>
      </VolumeRow>
    </Root>
  )
}

const Root = styled.div`
  display: grid;

  flex-shrink: 0;
  border-radius: 12px;
  border: 1px solid var(--backgroundA-2, rgba(255, 255, 255, 0.04));
  background: var(--appearanceA-7);

  gap: 6px;
  padding: 6px;
`

const PriceRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;

  gap: 3px;
`

const VolumeRow = styled.div`
  display: grid;
  grid-template-columns: 1fr auto 1fr auto 1fr;
`

const Price = styled.div`
  display: flex;
  flex-direction: column;
  text-align: center;
  justify-content: center;
  padding: 10px;

  background: var(--background-1);

  flex-shrink: 0;
  gap: 2px;
  border-radius: 8px;
`

const PriceVolume = styled(Price)`
  background: transparent;

  gap: 0;
`

const Label = styled(Typography).attrs({
  variant: 'description-semi-bold',
  component: 'span',
})`
  color: var(--text-9);
  text-align: center;
`

const PriceValue = styled.span`
  color: var(--text-12);

  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
`

const VolumeValue = styled(Typography).attrs({
  variant: 'body2-bold',
  component: 'span',
})`
  color: var(--text-12);
`
